import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, API_BASE, tokenStore } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Download, Info, Layers, AlertTriangle, Loader2, Settings as SettingsIcon, Sliders } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KpiOverrideDialog } from "@/components/monthly/KpiOverrideDialog";

function istToday() {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
}

function ymOptions() {
  const out = [];
  const now = istToday();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ value: ym, label: d.toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
}

export default function MonthlyReportsPage() {
  const [groups, setGroups] = useState([]);
  const [parks, setParks] = useState([]);
  const [month, setMonth] = useState(() => {
    // default to previous month
    const d = istToday();
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState({}); // {group_id: true}
  const [kpiGroup, setKpiGroup] = useState(null);

  const months = useMemo(() => ymOptions(), []);
  const parksById = useMemo(() => Object.fromEntries(parks.map((p) => [p.id, p])), [parks]);

  useEffect(() => {
    (async () => {
      try {
        const [g, p] = await Promise.all([
          api.get("/admin/report-groups").then((r) => r.data),
          api.get("/parks").then((r) => r.data),
        ]);
        setGroups(g);
        setParks(p);
      } catch (e) {
        toast.error("Failed to load report groups");
      }
    })();
  }, []);

  async function downloadGroup(g) {
    if (!g.park_ids?.length) {
      toast.error("This group has no parks assigned. Edit it in Settings → Report Groups.");
      return;
    }
    setBusy((b) => ({ ...b, [g.id]: true }));
    try {
      const url = `${API_BASE}/admin/monthly-report/generate?group_id=${encodeURIComponent(g.id)}&month=${month}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const safe = g.name.replace(/[^A-Za-z0-9]+/g, "_").slice(0, 60);
      a.download = `monthly_report_${safe}_${month}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Failed to generate report");
    } finally {
      setBusy((b) => {
        const n = { ...b };
        delete n[g.id];
        return n;
      });
    }
  }

  return (
    <AppShell
      title="Monthly Reports"
      subtitle="Auto-generated SIPCOT monthly compliance reports — one DOCX per group"
    >
      <div className="space-y-5">
        <Alert className="border-sky-200 bg-sky-50/60" data-testid="mr-info-alert">
          <Info className="h-4 w-4 text-sky-700" />
          <AlertTitle className="text-sky-900">How this works</AlertTitle>
          <AlertDescription className="text-sky-900/90 text-[13px] leading-relaxed">
            Reports are built entirely from data already captured in the app — attendance, holidays,
            cleaning checklists and monthly feedback uploads. KPI scores are auto-computed; for any
            (park, month) combination you can use the <b>Edit KPIs</b> button on each card to override
            specific KPI percentages and click <b>Final Accept</b> so the next DOCX uses your values.
            Each report group produces one DOCX containing one section per park.
            <br />
            <span className="mt-1.5 inline-block">
              <strong>Park groupings</strong> can be edited or extended in&nbsp;
              <Link to="/admin/settings" className="font-medium underline underline-offset-2 hover:text-sky-700">
                Settings → Report Groups
              </Link>.
            </span>
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Report month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[220px]" data-testid="mr-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Link to="/admin/settings#report-groups" className="ml-auto">
            <Button variant="outline" size="sm" data-testid="mr-edit-groups-btn">
              <SettingsIcon className="mr-1.5 h-4 w-4" /> Edit groups in Settings
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groups.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
              No report groups configured. Create one in Settings → Report Groups.
            </CardContent></Card>
          ) : groups.map((g) => {
            const parkNames = (g.park_ids || []).map((id) => parksById[id]).filter(Boolean);
            const isBusy = !!busy[g.id];
            return (
              <Card key={g.id} data-testid={`mr-card-${g.id}`} className="flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-indigo-100 text-indigo-700">
                      <Layers className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-[15px] leading-snug line-clamp-2">{g.name}</CardTitle>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{parkNames.length} park{parkNames.length !== 1 ? "s" : ""}</span>
                        {!g.active && <Badge variant="outline" className="text-rose-700 border-rose-300">inactive</Badge>}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    {parkNames.length === 0 ? (
                      <span className="inline-flex items-center gap-1 text-[12px] text-rose-700">
                        <AlertTriangle className="h-3.5 w-3.5" /> No parks assigned
                      </span>
                    ) : parkNames.map((p) => (
                      <Badge key={p.id} variant="outline" className="text-[10px] font-normal bg-slate-50">
                        <span className="font-mono mr-1 text-slate-500">{p.code}</span>{p.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-auto flex flex-col gap-2">
                    <Button
                      onClick={() => downloadGroup(g)}
                      disabled={isBusy || parkNames.length === 0}
                      data-testid={`mr-download-${g.id}`}
                    >
                      {isBusy ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>
                      ) : (
                        <><Download className="mr-2 h-4 w-4" /> Download DOCX</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setKpiGroup(g)}
                      disabled={parkNames.length === 0}
                      data-testid={`mr-edit-kpis-${g.id}`}
                    >
                      <Sliders className="mr-2 h-4 w-4" /> Edit KPIs
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      <KpiOverrideDialog
        open={!!kpiGroup}
        onOpenChange={(v) => !v && setKpiGroup(null)}
        group={kpiGroup}
        parks={parks}
        month={month}
        monthLabel={months.find((m) => m.value === month)?.label || month}
      />
    </AppShell>
  );
}
